import { cn } from "@/lib/cn";
import { TeamLabel } from "@/shared";
import { Icon } from "../Icon";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "ternary"
  | "danger"
  | "link"
  | "pro";
type ButtonSize = "small" | "medium" | "large" | "xlarge";

const base =
  "tw-leading-normal tw-font-semibold tw-font-sans tw-whitespace-nowrap tw-relative tw-rounded tw-flex tw-items-center tw-justify-center tw-transition-[background,border-color] tw-duration-200 tw-ease-in-out focus:input-outline";
const sizes: Record<string, string> = {
  small: "tw-text-[11px] tw-h-6 tw-px-2",
  medium: "tw-text-[13px] tw-h-8 tw-px-3",
  large: "tw-text-[13px] tw-h-10 tw-px-4",
  xlarge: "tw-text-[18px] tw-h-12 tw-px-6",
};
const variants: Record<string, string> = {
  primary:
    "tw-text-white disabled:tw-border-blue-300 [&_i]:tw-text-white disabled:tw-bg-blue-200 disabled:tw-cursor-not-allowed disabled:tw-text-blue-700 tw-border tw-border-solid tw-border-primary-800 tw-bg-primary-base hover:tw-bg-primary-600 hover:tw-border-primary-700 tw-shadow-main",
  secondary:
    "tw-text-blue-base disabled:tw-border-[#e7e6ec7f] [&:disabled_i]:tw-text-blue-400 disabled:tw-bg-white disabled:tw-cursor-not-allowed disabled:tw-text-blue-400 tw-border tw-border-solid tw-border-[#e7e6ec7f] tw-bg-white hover:tw-bg-[#F4F3F6] hover:tw-border-blue-100 tw-shadow-main",
  danger:
    "tw-text-white [&:disabled_i]:tw-text-blue-400 tw-border tw-border-solid tw-border-[#B12636] tw-bg-[#D43548] hover:tw-bg-[#e53a4e] hover:tw-border-[#a42231] tw-shadow-main disabled:tw-border-blue-300 disabled:tw-bg-blue-200 disabled:tw-cursor-not-allowed disabled:tw-text-blue-700",
  link: "tw-text-primary-base tw-bg-transparent tw-border-none tw-shadow-none hover:tw-text-primary-500 tw-transition-colors tw-duration-200",
  pro: "tw-text-white tw-border tw-border-solid tw-border-[var(--pro-border-color)] tw-bg-[var(--pro-main-color)] hover:tw-opacity-80 tw-shadow-main tw-transition-opacity tw-duration-200 [&_i]:tw-text-white",
};

const iconStyle: Record<string, Record<string, string | number>> = {
  small: {
    size: 14,
    className: "tw-mr-1",
  },
  medium: {
    size: 16,
    className: "tw-mr-[6px]",
  },
  large: {
    size: 20,
    className: "tw-mr-[6px]",
  },
  xlarge: {
    size: 24,
    className: "tw-mr-2",
  },
};

export const Button = ({
  title,
  variant,
  size,
  icon,
  disabled,
  className,
  loading,
  teamLock,
  onClick,
}: {
  title: string;
  variant: ButtonVariant;
  size: ButtonSize;
  onClick: () => void;
  teamLock?: boolean;
  icon?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}) => {
  return (
    <button
      className={cn(
        base,
        sizes[size],
        variants[variant],
        className,
        loading && "tw-cursor-not-allowed",
      )}
      disabled={disabled}
      onClick={(e) => (disabled || loading ? e.preventDefault() : onClick())}
    >
      {icon && (
        <Icon
          icon={icon}
          size={iconStyle[size].size as number}
          className={iconStyle[size].className as string}
        />
      )}
      {title && <span>{title}</span>}
      {loading && <Spinner size={size} />}
      {teamLock && (
        <span className="team-lock -tw-mr-1 tw-ml-1 tw-flex tw-items-center tw-justify-center">
          <TeamLabel />
        </span>
      )}
    </button>
  );
};

const Spinner = ({ size }: { size: ButtonSize }) => {
  return (
    <span className="--spinner-wrapper tw-absolute tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-rounded tw-bg-white tw-bg-opacity-80">
      <span
        className={cn(
          "--spinner tw-size-6 tw-animate-spin-regular tw-rounded-full tw-border-[3px] tw-border-solid tw-border-primary-base tw-border-b-[transparent]",
          {
            "tw-size-4 tw-border-2": size === "small",
          },
        )}
      ></span>
    </span>
  );
};
